import { createApiCall } from 'services';

/**
 * VRG-720 slice 2 -- the two calls behind "claim on scan".
 *
 * Plain functions rather than react-query mutations: the claim drawer is a small step
 * machine that owns its own pending/error state, and a mutation's retry scheduler is
 * exactly what `useClaimTransferByToken` had to switch off (`retry: false`) because a
 * paused retry on a hidden tab leaves a claim hanging forever. `throwError: true` lets the
 * caller see the status and `details.reason`.
 */

/** Fields the public certificate payload carries when claim on scan is in play. */
export interface ClaimOnScanFields {
  /** The product has the switch on (says nothing about whether it is still unclaimed). */
  claim_on_scan?: boolean;
  /** Claim on scan is on AND the item is still unclaimed right now. */
  claimable?: boolean;
}

export type ClaimFailureReason =
  | 'ticket_invalid'
  | 'ticket_expired'
  | 'vt_unverified'
  | 'not_claimable'
  | 'already_claimed'
  | 'own_item'
  | 'unauthenticated'
  | 'unknown';

/**
 * Exchange a fresh-tap `vt` for a claim ticket. The server verifies the tap is genuine,
 * recent and for THIS certificate before it issues one, so a copied link cannot get here.
 */
export const mintClaimTicket = async (
  certificateId: string,
  vt: string,
): Promise<{ ticket: string; expires_at: string }> => {
  const response = await createApiCall({
    method: 'POST',
    url: `certificates/public/${certificateId}/claim-ticket`,
    data: { vt },
    throwError: true,
  });
  return response.data as { ticket: string; expires_at: string };
};

/** Complete the claim. Needs a signed-in owner session AND the ticket. */
export const claimOnScan = async (
  certificateId: string,
  ticket: string,
): Promise<{ certificate_id: string; transfer_id: string }> => {
  const response = await createApiCall({
    method: 'POST',
    url: `certificates/customer/${certificateId}/claim-on-scan`,
    data: { ticket },
    throwError: true,
  });
  return response.data as { certificate_id: string; transfer_id: string };
};

/**
 * Collapse whatever came back into the one discriminator the UI branches on. Anything the
 * server did not label (a network failure, a 500) is `unknown`, which the caller words as
 * "try again" rather than guessing a cause.
 */
export const claimFailureReason = (error: unknown): ClaimFailureReason => {
  const response = (error as { response?: { status?: number; data?: any } } | null)?.response;
  const reason = response?.data?.details?.reason;
  if (
    reason === 'ticket_invalid' ||
    reason === 'ticket_expired' ||
    reason === 'vt_unverified' ||
    reason === 'not_claimable' ||
    reason === 'already_claimed' ||
    reason === 'own_item'
  ) {
    return reason;
  }
  if (response?.status === 401 || response?.status === 403) return 'unauthenticated';
  return 'unknown';
};
